import { Button, Icon, Modal } from '@octano/global-ui';
import { Trans, useTranslation } from 'react-i18next';

import { CONTACT_EMAIL } from '../../config/constants';
import { removeJWT } from '../../utils/auth';

interface Props {
  isOpen: boolean;
  targetPortalUrl?: string;
  email?: string;
  onClose: () => void;
}

/**
 * Modal mostrado cuando el `id_token` valida pero la cuenta no tiene el
 * scope del portal actual (NOT_SCOPE). Ofrece dos salidas:
 * - "Reintentar inicio de sesión" → limpia la sesión local y cierra el modal.
 * - "Ir a Campus" → navega al portal correcto. La URL la resuelve el
 *   backend desde `tenant.domains` y la propaga vía query param.
 */
export default function DifferentPortalModal({
  isOpen,
  targetPortalUrl,
  email,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const closeSession = () => {
    removeJWT();
    localStorage.removeItem('authProvider');
    onClose();
  };

  const goToPortal = () => {
    if (targetPortalUrl) {
      window.location.assign(targetPortalUrl);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <div className="d-flex flex-column align-items-center">
        <Icon name="warning" color="primary" size={50} />
        <h1 className="text-dark fs-20 mb-0 mt-4 text-center">
          {t('login.microsoftEntraId.different_portal_modal.title')}
        </h1>

        {email ? (
          <div
            className="d-inline-flex align-items-center bg-light rounded-pill px-3 py-1 mt-3"
            style={{ gap: 8 }}
          >
            <Icon name="person_record" size={16} />
            <span className="fs-14 text-muted">{email}</span>
          </div>
        ) : null}

        <div
          className="w-100 mt-4 p-3 rounded d-flex align-items-start"
          style={{
            backgroundColor: '#E8F1FB',
            border: '1px solid #C2D9F5',
            gap: 12,
          }}
        >
          <Icon name="information" color="primary" size={20} />
          <p className="fs-14 mb-0 text-dark">
            <Trans
              i18nKey="login.microsoftEntraId.different_portal_modal.alert"
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        <div
          className="w-100 mt-4"
          style={{
            display: 'grid',
            gridTemplateColumns: targetPortalUrl ? '1fr 1fr' : '1fr',
            gap: 12,
          }}
        >
          <Button
            outlined
            text={t('login.microsoftEntraId.different_portal_modal.retry')}
            className="text-uppercase"
            onClick={closeSession}
          />
          {targetPortalUrl ? (
            <Button
              text={t(
                'login.microsoftEntraId.different_portal_modal.go_to_portal',
              )}
              className="text-uppercase"
              onClick={goToPortal}
            />
          ) : null}
        </div>

        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="mt-3 fs-14 text-decoration-underline"
        >
          {t('login.microsoftEntraId.different_portal_modal.contact_support')}
        </a>
      </div>
    </Modal>
  );
}
